import { useLayoutEffect, useState } from 'react';

/**
 * Tracks the rounded pixel size of the observed element: a chart is drawn
 * at the size its body has, which changes with a resize and with the flex
 * column reflowing when a row is added or removed.
 */
export function useElementSize(ref: React.RefObject<HTMLElement | null>): {
  width: number;
  height: number;
} {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // The first measurement applies immediately (a chart sized from it
    // shouldn't wait to appear); subsequent ones are debounced so a live
    // resize doesn't redraw the chart on every observer callback.
    let timer: number | undefined;
    let measured = false;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      const next = {
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      };
      const apply = () =>
        setSize((current) =>
          current.width === next.width && current.height === next.height
            ? current
            : next,
        );
      if (!measured) {
        measured = true;
        apply();
        return;
      }
      window.clearTimeout(timer);
      timer = window.setTimeout(apply, 150);
    });
    observer.observe(el);
    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [ref]);

  return size;
}

/** The rounded pixel height of the observed element (see {@link useElementSize}). */
export function useElementHeight(
  ref: React.RefObject<HTMLElement | null>,
): number {
  return useElementSize(ref).height;
}
